<template>
  <div class="col-12 col-xl-12">
    <!-- Filters -->
    <div class="container-fluid px-0">
      <div class="row mb-4 g-2 p-3 p-xl-4">
        <!-- Academic Year Filter -->
        <div class="col-md-2 col-lg-2">
          <select
            class="form-select form-select-sm"
            v-model="localFilters.academic_year_id"
            @change="applyFilters"
          >
            <option value="">{{ $t('student_rewards.filters.all_years') }}</option>
            <option v-for="year in academicYears" :key="year.id" :value="year.id">
              {{ year.name }}
            </option>
          </select>
        </div>

        <!-- Class Filter -->
        <div class="col-md-2 col-lg-2">
          <select
            class="form-select form-select-sm"
            v-model="localFilters.class_id"
            @change="handleClassChange"
          >
            <option value="">{{ $t('student_rewards.filters.all_classes') }}</option>
            <option v-for="cls in classes" :key="cls.id" :value="cls.id">
              {{ cls.name }}
            </option>
          </select>
        </div>

        <!-- Group Filter -->
        <div class="col-md-2 col-lg-2">
          <select
            class="form-select form-select-sm"
            v-model="localFilters.class_group_id"
            @change="applyFilters"
          >
            <option value="">{{ $t('student_rewards.filters.all_groups') }}</option>
            <option v-for="group in groups" :key="group.id" :value="group.id">
              {{ group.name }}
            </option>
          </select>
        </div>

        <!-- Reset Filter (desktop) -->
        <div class="col-md-1 d-none d-md-block">
          <button 
            class="btn btn-outline-secondary btn-sm w-100" 
            @click="resetFilters"
            :title="$t('filters.resetTitle')"
          >
            <i class="fas fa-filter-circle-xmark"></i>
          </button>
        </div>

        <!-- Search -->
        <div class="col-md-5 col-lg-5 position-relative">
          <input
            type="text"
            class="form-control form-control-sm ps-5"
            :placeholder="$t('student_rewards.filters.search_placeholder')"
            v-model="localFilters.search"
            @input="handleSearchInput"
            :dir="locale === 'ar' ? 'rtl' : 'ltr'"
            style="padding-right: 12px"
          />
          <span class="position-absolute start-0 top-50 translate-middle-y ps-3">
            <i class="fas fa-search"></i>
          </span>
        </div>

        <!-- Reset Filter (mobile) -->
        <div class="col-md-1 d-md-none">
          <button class="btn btn-outline-secondary btn-sm w-100" @click="resetFilters">
            <i class="fas fa-filter-circle-xmark"></i> 
          </button>
        </div>

     
      </div>
    </div>
    
    <div class="card mb-3 mb-xxl-5 me-xxl-4" style="margin-top: 4rem; overflow: visible">
      <!-- Card Header -->
      <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2" style="height: 60px">
        <div class="bg-gradient-primary shadow-primary border-radius-lg pt-4 pb-3 h-100">
          <h6 class="text-white text-capitalize ps-3 pe-3" style="font-size: 1rem">
            {{ $t('student_rewards.title') }}
          </h6>
        </div>
      </div>

      <!-- Card Body -->
      <div class="card-body px-0 pb-2 pe-xl-3">
        <!-- Students List -->
        <div class="table-responsive p-1 p-xl-4">
          <LoadingSpinner
            v-if="loadingStudents"
            :message="$t('common.loading')"
            class="py-5"
          />

          <template v-else>
            <!-- Mobile View -->
            <div class="d-block d-xl-none">
              <div
                v-if="students.length === 0"
                class="text-center py-3 text-muted"
              >
                <i class="fas fa-user-graduate fa-2x mb-2"></i>
                <p>{{ $t('student_rewards.no_students') }}</p>
              </div>

              <div
                v-for="student in students"
                :key="'mobile-' + student.id"
                class="card mb-3 mx-2"
              >
                <div class="card-body py-2">
                  <div class="d-flex align-items-center">
                    <div class="flex-grow-1">
                      <h6 class="mb-0 text-sm">{{ student.full_name }}</h6>
                      <p class="text-xs text-muted mb-0">
                        {{ student.current_assignment?.class?.name || $t('student_rewards.no_class') }}
                      </p>
                    </div>
                    <div>
                      <span
                        class="badge badge-sm"
                        :class="student.gender === 'M' ? 'bg-primary' : 'bg-secondary'"
                      >
                        {{ student.gender === 'M' ? $t('student_rewards.male') : $t('student_rewards.female') }}
                      </span>
                    </div>
                  </div>
                  
                  <div class="mt-2">
                    <div class="d-flex justify-content-around">
                      <div class="text-center">
                        <span class="badge bg-success d-block mb-1">+{{ student.positive_points || 0 }}</span>
                        <small class="text-muted">{{ $t('student_rewards.positive') }}</small>
                      </div>
                      <div class="text-center">
                        <span class="badge bg-danger d-block mb-1">-{{ student.negative_points || 0 }}</span>
                        <small class="text-muted">{{ $t('student_rewards.negative') }}</small>
                      </div>
                      <div class="text-center">
                        <span class="badge d-block mb-1" :class="getTotalPointsClass(student.total_points || 0)">
                          {{ student.total_points || 0 }}
                        </span>
                        <small class="text-muted">{{ $t('student_rewards.total') }}</small>
                      </div>
                    </div>
                  </div>
                  
                  <div class="d-flex justify-content-between align-items-center mt-2">
                    <span class="text-xs">
                      <i class="fas fa-id-card me-1"></i> 
                      {{ student.national_num || student.passport_num || $t('student_rewards.no_id') }}
                    </span>

                    <div class="d-inline-flex">
                      <button
                        @click="viewRewardHistory(student.id)"
                        class="btn btn-link text-info p-0 mx-1"
                        :title="$t('student_rewards.view_history')"
                      >
                        <i class="fas fa-history fa-xs"></i>
                      </button>

                      <button
                        @click="openAssignRewardsDialog(student)"
                        class="btn btn-link text-primary p-0 mx-1"
                        :title="$t('student_rewards.assign_points')"
                        v-can="'student_rewards.create'"
                      >
                        <i class="fas fa-star fa-xs"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Desktop View -->
            <table class="table align-items-center mb-0 d-none d-xl-table">
              <thead>
                <tr>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7">
                    {{ $t('student_rewards.student') }}
                  </th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center">
                    {{ $t('student_rewards.class') }}
                  </th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center">
                    {{ $t('student_rewards.gender') }}
                  </th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center">
                    {{ $t('student_rewards.positive_points') }}
                  </th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center">
                    {{ $t('student_rewards.negative_points') }}
                  </th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center">
                    {{ $t('student_rewards.total_points') }}
                  </th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center pe-4">
                    {{ $t('student_rewards.actions') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!loadingStudents && students.length === 0">
                  <td colspan="7" class="text-center py-4 text-muted">
                    <i class="fas fa-user-graduate fa-2x mb-2"></i>
                    <p>{{ $t('student_rewards.no_students') }}</p>
                  </td>
                </tr>

                <template v-if="!loadingStudents">
                  <tr v-for="student in students" :key="student.id">
                    <td>
                      <div class="d-flex px-3 py-1">
                        <div class="d-flex flex-column justify-content-center">
                          <h6 class="mb-0 text-md">{{ student.full_name }}</h6>
                          <p class="text-xs text-secondary mb-0">
                            {{ student.national_num || student.passport_num || $t('student_rewards.no_id') }}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td class="align-middle text-center">
                      <span class="text-xs font-weight-bold">
                        {{ student.current_assignment?.class?.name || $t('student_rewards.no_class') }}
                        <span v-if="student.current_assignment?.group" class="text-xs text-secondary">
                          ({{ student.current_assignment.group.name }})
                        </span>
                      </span>
                    </td>
                    <td class="align-middle text-center">
                      <span
                        class="badge badge-sm"
                        :class="student.gender === 'M' ? 'bg-primary' : 'bg-secondary'"
                      >
                        {{ student.gender === 'M' ? $t('student_rewards.male') : $t('student_rewards.female') }}
                      </span>
                    </td>
                    <td class="align-middle text-center">
                      <span class="badge bg-success">
                        +{{ student.positive_points || 0 }}
                      </span>
                    </td>
                    <td class="align-middle text-center">
                      <span class="badge bg-danger">
                        -{{ student.negative_points || 0 }}
                      </span>
                    </td>
                    <td class="align-middle text-center">
                      <span class="badge" :class="getTotalPointsClass(student.total_points || 0)">
                        {{ student.total_points || 0 }}
                      </span>
                    </td>
                    <td class="align-middle text-end pe-4">
                      <div class="d-inline-flex">
                        <button
                          @click="viewRewardHistory(student.id)"
                          class="btn btn-link text-info p-1 mx-1"
                          :title="$t('student_rewards.view_history')"
                        >
                          <i class="fas fa-history fa-sm"></i>
                        </button>
                        <button
                          @click="openAssignRewardsDialog(student)"
                          class="btn btn-link text-primary p-1 mx-1"
                          :title="$t('student_rewards.assign_points')"
                          v-can="'student_rewards.create'"
                        >
                          <i class="fas fa-star fa-sm"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </template>

          <!-- Pagination -->
          <div class="px-3 px-xl-4">
            <PaginationControls
              v-if="pagination.total > 0"
              :pagination="pagination"
              @page-changed="handlePageChange"
              @per-page-changed="handlePerPageChange"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Assign Rewards Modal -->
    <AssignRewardsModal
      ref="assignRewardsModal"
      :student="selectedStudent"
      @rewards-assigned="handleRewardsAssigned"
    />
  </div>
</template>
<script setup>
import { ref, onMounted, watch, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useToast } from 'vue-toastification'
import api from '@/api'
import PaginationControls from '@/components/common/PaginationControls.vue'
import LoadingSpinner from '@/components/common/LoadingSpinner.vue'
import AssignRewardsModal from '@/components/features/student-rewards/dialogs/AssignRewardsModal.vue'

const router = useRouter()
const { t, locale } = useI18n()
const toast = useToast()

// State
const loadingStudents = ref(false)
const students = ref([])
const academicYears = ref([])
const classes = ref([])
const groups = ref([])
const pagination = ref({})
const selectedStudent = ref(null)
const assignRewardsModal = ref(null)

let debounceTimeout = null

// Filters
const filters = ref({
  academic_year_id: '',
  class_id: '',
  class_group_id: '',
  search: ''
})

const localFilters = ref({
  ...JSON.parse(JSON.stringify(filters.value))
})

const debounce = (fn, delay) => {
  return (...args) => {
    if (debounceTimeout) {
      clearTimeout(debounceTimeout)
    }
    
    debounceTimeout = setTimeout(() => {
      fn(...args)
      debounceTimeout = null
    }, delay)
  }
}

onUnmounted(() => {
  if (debounceTimeout) {
    clearTimeout(debounceTimeout)
  }
})

const handleSearchInput = () => {
  debouncedSearch()
}

const debouncedSearch = debounce(() => {
  applyFilters()
}, 500)

const applyFilters = () => {
  filters.value = { ...localFilters.value }
  fetchStudents(1)
}

const resetFilters = () => {
  if (debounceTimeout) {
    clearTimeout(debounceTimeout)
    debounceTimeout = null
  }
  
  localFilters.value = {
    academic_year_id: '',
    class_id: '',
    class_group_id: '',
    search: ''
  }
  
  applyFilters()
}

const fetchAcademicYears = async () => {
  try {
    const response = await api.get('/academic-years')
    academicYears.value = response.data.data
  } catch (error) {
    console.error('Error fetching academic years:', error)
  }
}

const fetchClasses = async () => {
  try {
    const response = await api.get('/classes')
    classes.value = response.data.data
  } catch (error) {
    console.error('Error fetching classes:', error)
  }
}

const fetchGroups = async () => {
  if (!localFilters.value.class_id) {
    groups.value = []
    return
  }
  
  try {
    const response = await api.get(`/classes/${localFilters.value.class_id}/groups`)
    groups.value = response.data.data
  } catch (error) {
    console.error('Error fetching groups:', error)
  }
}

const handleClassChange = () => {
  fetchGroups()
  applyFilters()
}

const fetchStudents = async (page = 1, perPage = 10) => {
  loadingStudents.value = true
  try {
    const params = {
      page,
      per_page: perPage,
      academic_year_id: filters.value.academic_year_id || null,
      class_id: filters.value.class_id || null,
      class_group_id: filters.value.class_group_id || null,
      search: filters.value.search || null
    }
    
    const response = await api.get('/students', { params })
    students.value = response.data.data
    pagination.value = response.data.meta || {}
    
    // OPTIMIZED: Get points for all students in ONE API call
    await calculateBatchStudentPoints()
    
  } catch (error) {
    console.error('Error fetching students:', error)
    toast.error(t('errors.fetch_failed'))
  } finally {
    loadingStudents.value = false
  }
}

const calculateBatchStudentPoints = async () => {
  if (students.value.length === 0) return
  
  try {
    const studentIds = students.value.map(student => student.id)
    
    const response = await api.post('/student-rewards/batch-points-summary', {
      student_ids: studentIds,
      academic_year_id: filters.value.academic_year_id || null
    })
    
    const pointsMap = {}
    response.data.data.forEach(item => {
      pointsMap[item.id] = {
        positive_points: item.positive_points,
        negative_points: item.negative_points,
        total_points: item.total_points
      }
    })
    
    // Assign points to students
    students.value.forEach(student => {
      const points = pointsMap[student.id] || {
        positive_points: 0,
        negative_points: 0,
        total_points: 0
      }
      
      student.positive_points = points.positive_points
      student.negative_points = points.negative_points
      student.total_points = points.total_points
    })
    
  } catch (error) {
    console.error('Error calculating batch points:', error)
    students.value.forEach(student => {
      student.positive_points = 0
      student.negative_points = 0
      student.total_points = 0
    })
  }
}

// Event handlers
const handlePageChange = (page) => {
  fetchStudents(page)
}

const handlePerPageChange = (perPage) => {
  fetchStudents(1, perPage)
}

const openAssignRewardsDialog = (student = null) => {
  selectedStudent.value = student
  assignRewardsModal.value.openDialog(student)
}

const viewRewardHistory = (studentId) => {
  router.push(`/students/${studentId}/reward-history`)
}

const handleRewardsAssigned = () => {
  fetchStudents(pagination.value.current_page || 1)
  toast.success(t('student_rewards.messages.rewards_assigned'))
}

// Helper functions
const getTotalPointsClass = (points) => {
  if (points >= 100) return 'bg-success'
  if (points >= 50) return 'bg-info'
  if (points >= 0) return 'bg-primary'
  return 'bg-danger'
}

// Lifecycle
onMounted(() => {
  fetchAcademicYears()
  fetchClasses()
  fetchStudents(1)
})

// Watchers
watch(filters, () => {
  fetchStudents(1)
}, { deep: true })

watch(() => localFilters.value, () => {
  applyFilters()
}, { deep: true })
</script>